import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary } from '@liveauctioneers/hammer-ui-core/text';
import { type ComponentProps, useCallback } from 'react';

import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { SearchUrlTrigger } from '@/types/search/enums/searchUrlTrigger';
import { SpellCorrectionUrlValue } from '@/types/search/enums/spellCorrectionUrlValue';
import { useTrackSpellCorrectionRevertClickedAnalytics } from '@/utils/amplitude/analytics/searchPage/trackSpellCorrectionRevertClicked.analytics';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getSearchUrlWithParams } from '@/utils/urls';

type Props = {
    /**
     * The keyword the results are for.
     */
    correctedSearchTerm: string;
    /**
     * The keyword as the person typed it, before search-party corrected it.
     */
    originalSearchTerm: string;
};

export const SpellCorrectionMessage = ({ correctedSearchTerm, originalSearchTerm }: Props) => {
    const formatMessage = useAppFormatMessage();
    const trackSpellCorrectionRevertClicked = useTrackSpellCorrectionRevertClickedAnalytics();
    const displayOriginalSearchTerm = cleanSearchTerms(originalSearchTerm);
    const searchInsteadHref = useLocalizedPath(
        getSearchUrlWithParams({
            keyword: originalSearchTerm,
            spellCorrection: SpellCorrectionUrlValue.Off,
            trigger: SearchUrlTrigger.SpellCorrection,
        })
    );

    const handleSearchInsteadClick = useCallback<ComponentProps<typeof Link>['onClick']>(() => {
        trackSpellCorrectionRevertClicked({ correctedQuery: correctedSearchTerm, originalQuery: originalSearchTerm });
    }, [correctedSearchTerm, originalSearchTerm, trackSpellCorrectionRevertClicked]);

    return (
        <div
            className="mb-sm"
            data-testid="spell-correction-message"
        >
            <BodyPrimary>
                {formatMessage(['searchPage', 'searchResults', 'searchInsteadFor'], {
                    values: {
                        originalSearchTerm: (
                            <Link
                                href={searchInsteadHref}
                                key="search-instead-link"
                                onClick={handleSearchInsteadClick}
                            >
                                {displayOriginalSearchTerm}
                            </Link>
                        ),
                    },
                })}
            </BodyPrimary>
        </div>
    );
};
